import { useGaiaWebsocket } from "@expert/gaia";
import { useDebouncedCallback } from "@mantine/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { SpellCheckResponse, SpellCheckStatus } from "@/sdk/agent/chat/types";

import { getRootDispatcher } from "@/analytics";
import { useLogger } from "@/sdk";
import { selectSpellCheck, useChatStore } from "@/sdk/agent/chat";

import type { UseSpellCheckOptions, UseSpellCheckResult } from "./types";

import { SPELLCHECK_CONFIG } from "./config";
import { gaiaSpellCheck } from "./gaiaSpellCheck";
import { getSpellCheckMutationKey } from "./types";

type MutationContext = { checkType: "background" | "immediate" };

export function useSpellCheck({
    chatId,
    sessionId,
    partner,
    enabled,
    config,
    localInput,
    sendMessage,
    spellCheckMetadataRef,
    onCancelWithText,
}: UseSpellCheckOptions): UseSpellCheckResult {
    const logger = useLogger({ module: "useSpellCheck", supportTeam: "messaging" });
    const queryClient = useQueryClient();

    const websocketObj = useGaiaWebsocket();
    const sendJsonMessage = websocketObj?.sendJsonMessage ?? (() => {});
    const websocketLoading = websocketObj?.loading ?? true;

    const updateSpellCheckStore = useChatStore((state) => state.updateSpellCheck);
    const clearSpellCheckStore = useChatStore((state) => state.clearSpellCheck);
    const spellCheckState = useChatStore(selectSpellCheck(chatId));

    const resolvedConfig = useMemo(() => ({ ...SPELLCHECK_CONFIG, ...config }), [config]);
    const chatAnalyticDispatcher = useMemo(() => getRootDispatcher().withIdentities({ ChatId: chatId }), [chatId]);

    const latestContentRef = useRef("");
    const checkTypeRef = useRef<"background" | "immediate">("background");
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        spellCheckMetadataRef.current.spellcheckEnabled = enabled;
    }, [enabled, spellCheckMetadataRef]);

    const mutation = useMutation<SpellCheckResponse | undefined, Error, string, MutationContext>({
        mutationKey: getSpellCheckMutationKey(chatId),
        mutationFn: async (content: string) => {
            if (!sessionId || !chatId || !content.trim()) return undefined;
            if (abortControllerRef.current) abortControllerRef.current.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;
            try {
                return await gaiaSpellCheck({ partner, sessionId, chatId, message: content }, sendJsonMessage, logger, {
                    timeoutMs: resolvedConfig.apiTimeoutMs,
                    signal: controller.signal,
                });
            } finally {
                if (abortControllerRef.current === controller) abortControllerRef.current = null;
            }
        },
        onMutate: (content) => {
            logger.trace({ chatId, contentLength: content.length }, "Starting spellcheck");
            const checkType = checkTypeRef.current;
            updateSpellCheckStore(chatId, {
                status: "checking",
                originalText: content,
                showStatus: checkType === "immediate",
            });
            return { checkType };
        },
        onSuccess: (result, content, context) => {
            if (latestContentRef.current !== content) {
                logger.trace({ chatId }, "Spellcheck result discarded - content changed during check");
                return;
            }
            const checkType = context?.checkType ?? "background";
            const shouldShowStatus = checkType === "immediate";

            if (!result) {
                updateSpellCheckStore(chatId, {
                    status: "error",
                    errorMessage: "Spellcheck returned no result",
                    lastCheckedContent: content,
                    showStatus: shouldShowStatus,
                });
                const payload = {
                    chatId,
                    messageLength: content.length,
                    errorMessage: "Spellcheck returned no result",
                    checkType,
                };
                chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckPerformed", payload);
                return;
            }

            const newStatus: SpellCheckStatus = result.rewritingNeeded ? "suggestions" : "success";
            updateSpellCheckStore(chatId, {
                status: newStatus,
                result,
                lastCheckedContent: content,
                errorMessage: undefined,
                showStatus: shouldShowStatus,
            });
            const payload = {
                chatId,
                messageLength: content.length,
                rewritingNeeded: result.rewritingNeeded,
                issuesFound: result.highlightSnippets?.length ?? 0,
                checkType,
            };
            chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckPerformed", payload);
            logger.trace({ chatId, rewritingNeeded: result.rewritingNeeded }, "Spellcheck completed");
        },
        onError: (error, content, context) => {
            if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
                logger.trace({ chatId }, "Spellcheck request cancelled (new request started)");
                return;
            }
            const checkType = context?.checkType ?? "background";
            const errorType = error?.constructor?.name ?? typeof error;
            const errorMessage = error instanceof Error ? error.message : String(error);
            logger.warn(
                { chatId, errorType, errorMessage, contentLength: content.length, checkType, error },
                "Spellcheck failed",
            );

            updateSpellCheckStore(chatId, {
                status: "error",
                errorMessage: error instanceof Error ? error.message : "Spellcheck failed",
                lastCheckedContent: content,
                showStatus: checkType === "immediate",
            });
            const payload = { chatId, messageLength: content.length, errorMessage, errorType, checkType };
            chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckPerformed", payload);
        },
    });

    const debouncedCheck = useDebouncedCallback((content: string) => {
        const trimmed = content.trim();
        if (!enabled || !trimmed) return;
        if (trimmed.length < resolvedConfig.minCharsForBackgroundCheck) {
            logger.trace(
                { chatId, contentLength: trimmed.length, minChars: resolvedConfig.minCharsForBackgroundCheck },
                "Skipping background spellcheck - content too short",
            );
            return;
        }
        if (spellCheckState?.lastCheckedContent === content) return;
        if (!sessionId) {
            logger.warn({ chatId }, "Skipping spellcheck - sessionId not available");
            return;
        }
        if (websocketLoading) {
            logger.trace({ chatId }, "Skipping spellcheck - WebSocket not ready");
            return;
        }
        queryClient.cancelQueries({ queryKey: getSpellCheckMutationKey(chatId) });
        latestContentRef.current = content;
        checkTypeRef.current = "background";
        mutation.mutate(content);
    }, resolvedConfig.debounceMs);

    // returned from hook, called by parent on every input change
    const triggerSpellCheck = useCallback(
        (content: string) => {
            if (!enabled) return;
            latestContentRef.current = content;
            debouncedCheck(content);
        },
        [enabled, debouncedCheck],
    );

    // used in handleSubmitMessage, captures reactive state so closure always sees current values
    const checkSpellingImmediate = useCallback(
        async (content: string): Promise<SpellCheckResponse | undefined> => {
            const trimmed = content.trim();
            if (!enabled || !trimmed) return undefined;
            if (!sessionId) {
                logger.warn({ chatId }, "Cannot run spellcheck - sessionId not available");
                return undefined;
            }
            if (websocketLoading) {
                logger.warn({ chatId }, "Cannot run spellcheck - WebSocket not ready");
                return undefined;
            }
            if (
                spellCheckState?.lastCheckedContent === content &&
                spellCheckState?.result &&
                spellCheckState?.status !== "error"
            ) {
                return spellCheckState.result;
            }
            debouncedCheck.cancel();
            queryClient.cancelQueries({ queryKey: getSpellCheckMutationKey(chatId) });
            latestContentRef.current = content;
            checkTypeRef.current = "immediate";
            return mutation.mutateAsync(content);
        },
        [enabled, sessionId, websocketLoading, chatId, spellCheckState, debouncedCheck, mutation, queryClient, logger],
    );

    // returned from hook, called by parent on message send to reset state
    const clearSpellCheck = useCallback(() => {
        debouncedCheck.cancel();
        clearSpellCheckStore(chatId);
    }, [chatId, clearSpellCheckStore, debouncedCheck]);

    useEffect(() => {
        return () => {
            debouncedCheck.cancel();
        };
    }, [debouncedCheck]);

    const handleSubmitMessage = useCallback(async () => {
        if (!enabled) {
            spellCheckMetadataRef.current = { spellcheckEnabled: false, spellcheckStatus: "disabled" };
            void sendMessage(localInput);
            return;
        }

        const hasCheckedContent = spellCheckState?.lastCheckedContent === localInput;
        if (hasCheckedContent && spellCheckState?.result) {
            if (spellCheckState.result.rewritingNeeded) {
                updateSpellCheckStore(chatId, { status: "suggestions", showStatus: true });
                //TODO: if component unmounted we should show some indicator for background spellchecks that prevent message sending
                const payload = {
                    chatId,
                    messageLength: localInput.length,
                    issuesFound: spellCheckState.result.highlightSnippets?.length ?? 0,
                };
                chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckSuggestionsShown", payload);
                return;
            }
            updateSpellCheckStore(chatId, { status: "success", showStatus: true });
            spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "success" };
            void sendMessage(localInput);
            return;
        }

        try {
            const result = await checkSpellingImmediate(localInput);
            if (!result) {
                updateSpellCheckStore(chatId, { status: "error", showStatus: true });
                spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "error" };
                void sendMessage(localInput);
                return;
            }
            if (result.rewritingNeeded) {
                updateSpellCheckStore(chatId, {
                    status: "suggestions",
                    result,
                    lastCheckedContent: localInput,
                    originalText: localInput,
                    showStatus: true,
                });
                //TODO: if component unmounted we should show some indicator for background spellchecks that prevent message sending
                const payload = {
                    chatId,
                    messageLength: localInput.length,
                    issuesFound: result.highlightSnippets?.length ?? 0,
                };
                chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckSuggestionsShown", payload);
                return;
            }
            updateSpellCheckStore(chatId, { status: "success" });
            spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "success" };
            void sendMessage(localInput);
        } catch (error) {
            logger.warn({ error, chatId }, "Spellcheck failed during send");
            updateSpellCheckStore(chatId, { status: "error" });
            spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "error" };
            void sendMessage(localInput);
        }
    }, [
        enabled,
        localInput,
        spellCheckState?.result,
        spellCheckState?.lastCheckedContent, // needed for the staleness check against localInput
        checkSpellingImmediate,
        updateSpellCheckStore,
        sendMessage,
        chatId,
        chatAnalyticDispatcher,
        spellCheckMetadataRef,
        logger,
    ]);

    // passed as onSendWithChanges prop to SpellCheckStatus
    const handleSendWithChanges = useCallback(() => {
        if (!spellCheckState?.result?.rewrittenText) return;
        const correctedText = spellCheckState.result.rewrittenText;
        const originalText = spellCheckState.originalText ?? "";
        const issuesFixed = spellCheckState.result.highlightSnippets?.length ?? 0;

        const payload = {
            chatId,
            originalMessageLength: originalText.length,
            correctedMessageLength: correctedText.length,
            issuesFixed,
        };
        chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckSendWithChanges", payload);

        spellCheckMetadataRef.current = {
            spellcheckEnabled: true,
            spellcheckStatus: "suggestions_accepted",
            spellcheckIssuesFixed: issuesFixed,
            originalMessageLength: originalText.length,
        };
        updateSpellCheckStore(chatId, { status: "success" });
        void sendMessage(correctedText);
    }, [
        spellCheckState?.result?.rewrittenText,
        spellCheckState?.originalText,
        spellCheckState?.result?.highlightSnippets,
        chatId,
        chatAnalyticDispatcher,
        updateSpellCheckStore,
        sendMessage,
        spellCheckMetadataRef,
    ]);

    // passed as onSendOriginal prop to SpellCheckStatus
    const handleSendOriginal = useCallback(() => {
        const originalText = spellCheckState?.originalText;
        if (!originalText) return;
        spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "skipped" };
        updateSpellCheckStore(chatId, { status: "success", showStatus: false });
        void sendMessage(originalText);
    }, [spellCheckState?.originalText, chatId, updateSpellCheckStore, sendMessage, spellCheckMetadataRef]);

    // passed as onCancel prop to SpellCheckStatus, restores input text and hides status UI
    const handleCancelSpellCheck = useCallback(() => {
        if (!spellCheckState?.result?.rewrittenText) {
            updateSpellCheckStore(chatId, { showStatus: false });
            return;
        }
        const payload = {
            chatId,
            messageLength: spellCheckState.originalText?.length ?? spellCheckState.result.rewrittenText.length,
            issuesFound: spellCheckState.result.highlightSnippets?.length ?? 0,
        };
        chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckCancelled", payload);
        const textToRestore = resolvedConfig.replaceInputWithCorrectedOnCancel
            ? spellCheckState.result.rewrittenText
            : (spellCheckState.originalText ?? spellCheckState.result.rewrittenText);
        onCancelWithText(textToRestore);
        updateSpellCheckStore(chatId, { showStatus: false });
    }, [
        spellCheckState?.result?.rewrittenText,
        spellCheckState?.originalText,
        spellCheckState?.result?.highlightSnippets,
        chatId,
        chatAnalyticDispatcher,
        onCancelWithText,
        updateSpellCheckStore,
        resolvedConfig.replaceInputWithCorrectedOnCancel,
    ]);

    // passed as onDismiss prop to SpellCheckStatus
    const handleDismissStatus = useCallback(
        () => updateSpellCheckStore(chatId, { showStatus: false }),
        [chatId, updateSpellCheckStore],
    );

    const displayStatus = enabled && spellCheckState?.showStatus ? (spellCheckState?.status ?? "idle") : "idle";

    return {
        triggerSpellCheck,
        spellCheckState,
        clearSpellCheck,
        isChecking: mutation.isPending || spellCheckState?.status === "checking",
        displayStatus,
        handleSubmitMessage,
        handleSendWithChanges,
        handleSendOriginal,
        handleCancelSpellCheck,
        handleDismissStatus,
    };
}
